import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  Sparkles, ArrowLeft, Send, Loader2, Bot, MessageSquare,
  CheckCircle2, AlertTriangle, RefreshCw,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_TONE = {
  P0: 'bg-red-600 text-white',
  P1: 'bg-amber-500 text-white',
  P2: 'bg-slate-400 text-white',
  P3: 'bg-slate-300 text-slate-700',
};

const CATEGORY_TONE = {
  bug: 'bg-red-50 text-red-700 border-red-200',
  question: 'bg-blue-50 text-blue-700 border-blue-200',
  feature: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  billing: 'bg-amber-50 text-amber-800 border-amber-200',
  integration: 'bg-violet-50 text-violet-800 border-violet-200',
  other: 'bg-slate-50 text-slate-700 border-slate-200',
};

export default function AdminSupportInbox() {
  const { token, user } = useAuth();
  const [data, setData] = useState({ kpis: {}, tickets: [] });
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState('');
  const [resolveOnReply, setResolveOnReply] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/admin/support/inbox`, auth);
      setData(r.data);
    } finally { setBusy(false); }
  }, [token]);

  useEffect(() => { if (user?.role === 'admin') load(); }, [user, load]);

  const openTicket = (t) => { setSelected(t); setReply(''); setResolveOnReply(false); };

  const sendReply = async () => {
    if (!selected || !reply.trim()) return;
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/support/tickets/${selected.id}/reply`,
        { body: reply.trim(), resolve: resolveOnReply },
        auth,
      );
      setSelected(r.data);
      setReply('');
      setResolveOnReply(false);
      await load();
    } finally { setBusy(false); }
  };

  const reTriage = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/support/tickets/${selected.id}/re-triage`,
        {},
        auth,
      );
      setSelected(r.data);
      await load();
    } finally { setBusy(false); }
  };

  if (user && user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center text-cn-text-muted">
        Admin access required.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cn-bg dark:bg-cn-bg-dark text-cn-text" data-testid="admin-support-inbox-page">
      <div className="border-b border-cn-line bg-white/70 dark:bg-slate-900/60 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MessageSquare className="w-5 h-5 text-violet-600" />
            <h1 className="text-lg font-semibold tracking-tight">Support Inbox</h1>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} className="cn-btn cn-btn-ghost text-xs" data-testid="admin-support-refresh">
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              Refresh
            </button>
            <Link to="/admin/dashboard" className="text-sm text-cn-text-muted hover:text-cn-text flex items-center gap-1.5">
              <ArrowLeft className="w-4 h-4" /> Admin
            </Link>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Open tickets" value={data.kpis?.open ?? 0} tone="slate" />
        <Kpi label="P0" value={data.kpis?.p0 ?? 0} tone={data.kpis?.p0 ? 'red' : 'slate'} />
        <Kpi label="P1" value={data.kpis?.p1 ?? 0} tone={data.kpis?.p1 ? 'amber' : 'slate'} />
        <Kpi label="Auto-resolved (24h)" value={data.kpis?.auto_resolved_last_24h ?? 0} tone="emerald" />
      </div>

      <div className="max-w-7xl mx-auto px-6 pb-10 grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Ticket list */}
        <div className="cn-card p-4 md:col-span-1" data-testid="admin-support-ticket-list">
          {(data.tickets || []).length === 0 && (
            <p className="text-xs text-cn-text-muted italic p-6 text-center">
              No open tickets. All caught up.
            </p>
          )}
          <ul className="space-y-2">
            {(data.tickets || []).map((t) => (
              <li key={t.id}>
                <button
                  onClick={() => openTicket(t)}
                  className={`block w-full text-left rounded-lg border p-2.5 transition-colors ${
                    selected?.id === t.id
                      ? 'border-violet-400 bg-violet-50/50 dark:bg-slate-800/60'
                      : 'border-cn-line hover:border-violet-300 hover:bg-slate-50/60 dark:hover:bg-slate-900/50'
                  }`}
                  data-testid={`admin-support-ticket-${t.id}`}
                >
                  <div className="flex items-center gap-1.5 flex-wrap mb-1">
                    <span className={`text-[0.6rem] font-bold px-1.5 py-0.5 rounded ${PRIORITY_TONE[t.priority] || PRIORITY_TONE.P2}`}>
                      {t.priority}
                    </span>
                    <span className={`text-[0.6rem] uppercase px-1.5 py-0.5 rounded border ${CATEGORY_TONE[t.category] || CATEGORY_TONE.other}`}>
                      {t.category}
                    </span>
                    <span className="text-[0.6rem] text-cn-text-muted ml-auto">
                      {t.status}
                    </span>
                  </div>
                  <div className="text-sm font-medium leading-snug truncate">
                    {t.subject}
                  </div>
                  <div className="text-[0.65rem] text-cn-text-muted mt-1 truncate">
                    {t.role_at_submission} · {(t.messages?.length || 0)} msg
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Detail */}
        <div className="md:col-span-2">
          {!selected && (
            <div className="cn-card p-8 text-center text-cn-text-muted text-sm">
              Pick a ticket to view Cara's suggested response + reply.
            </div>
          )}
          {selected && (
            <div className="cn-card p-6" data-testid="admin-support-detail">
              <div className="flex items-center gap-2 flex-wrap mb-3">
                <span className={`text-xs font-bold px-2 py-0.5 rounded ${PRIORITY_TONE[selected.priority] || PRIORITY_TONE.P2}`}>
                  {selected.priority}
                </span>
                <span className={`text-xs uppercase px-1.5 py-0.5 rounded border ${CATEGORY_TONE[selected.category] || CATEGORY_TONE.other}`}>
                  {selected.category}
                </span>
                <span className="text-xs text-cn-text-muted">· {selected.status}</span>
                <span className="text-xs text-cn-text-muted ml-auto">
                  {new Date(selected.created_at).toLocaleString()}
                </span>
              </div>
              <h2 className="text-xl font-semibold tracking-tight mb-4">
                {selected.subject}
              </h2>

              {/* Triage panel */}
              {selected.triage && (
                <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-3 mb-4">
                  <div className="text-[0.65rem] uppercase tracking-wider text-violet-700 mb-1 flex items-center gap-1.5">
                    <Bot className="w-3 h-3" /> Cara's triage
                    {selected.triage.auto_reply_confidence >= 0.82 && (
                      <span className="ml-1 text-emerald-700">· high confidence</span>
                    )}
                  </div>
                  <div className="text-sm mb-2">
                    <strong>Suggested response:</strong>{' '}
                    {selected.triage.suggested_response || '—'}
                  </div>
                  <div className="text-xs text-cn-text-muted flex flex-wrap gap-3">
                    <span>Confidence: {((selected.triage.auto_reply_confidence || 0) * 100).toFixed(0)}%</span>
                    {selected.triage.reasoning && (
                      <span>Reason: {selected.triage.reasoning}</span>
                    )}
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => setReply(selected.triage.suggested_response || '')}
                      className="cn-btn cn-btn-ghost !text-xs"
                      data-testid="admin-support-use-suggestion"
                    >
                      Use suggestion
                    </button>
                    <button
                      onClick={reTriage}
                      disabled={busy}
                      className="cn-btn cn-btn-ghost !text-xs"
                      data-testid="admin-support-retriage"
                    >
                      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                      Re-run triage
                    </button>
                  </div>
                </div>
              )}

              {/* Messages */}
              <div className="space-y-3 mb-4">
                {(selected.messages || []).map((m) => (
                  <div
                    key={m.id}
                    className={`rounded-xl px-4 py-3 border ${
                      m.from_role === 'cara' || m.from_role === 'admin'
                        ? 'bg-violet-50/50 border-violet-200 dark:bg-slate-800/60'
                        : 'bg-slate-50 border-cn-line dark:bg-slate-900/50'
                    }`}
                  >
                    <div className="text-[0.65rem] uppercase tracking-wider text-cn-text-muted mb-1 flex items-center gap-1.5">
                      {m.from_role === 'cara' ? <><Bot className="w-3 h-3 text-violet-600" /> Cara AI</> :
                        m.from_role === 'admin' ? 'Admin' : m.from_role}
                    </div>
                    <div className="text-sm whitespace-pre-wrap">{m.body}</div>
                  </div>
                ))}
              </div>

              {selected.status !== 'closed' && (
                <div className="border-t border-cn-line pt-3 space-y-2">
                  <textarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    placeholder="Type a reply…"
                    rows={3}
                    className="w-full rounded-lg border border-cn-line bg-white dark:bg-slate-900 px-3 py-2 text-sm"
                    data-testid="admin-support-reply-input"
                  />
                  <div className="flex items-center justify-between">
                    <label className="text-xs flex items-center gap-1.5 text-cn-text-muted cursor-pointer">
                      <input
                        type="checkbox"
                        checked={resolveOnReply}
                        onChange={(e) => setResolveOnReply(e.target.checked)}
                        data-testid="admin-support-resolve-check"
                      />
                      Resolve on send
                    </label>
                    <button
                      onClick={sendReply}
                      disabled={busy || !reply.trim()}
                      className="cn-btn cn-btn-primary"
                      data-testid="admin-support-reply-send"
                    >
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      Send reply
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, tone }) {
  const map = {
    red: 'text-red-700', amber: 'text-amber-700',
    emerald: 'text-emerald-700', slate: 'text-slate-700',
  };
  return (
    <div className="cn-card p-4">
      <div className="text-[0.65rem] uppercase tracking-wider text-cn-text-muted">
        {label}
      </div>
      <div className={`text-2xl font-semibold mt-1 ${map[tone] || map.slate}`}>
        {value}
      </div>
    </div>
  );
}
